import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { isValidPhoneNumber } from "libphonenumber-js";
import { startAuthentication } from "@simplewebauthn/browser";
import AuthService from "../../../api/services/AuthService";
import { alertErrorMessage, alertSuccessMessage } from "../../../customComponents/CustomAlertMessage";
import LoaderHelper from "../../../customComponents/Loading/LoaderHelper";
import { ProfileContext } from "../../../context/ProfileProvider";
import "../PasskeyPage/passkey.css";
import "./smsVerification.css";

/**
 * SMS Verification — Add or Change mobile flow.
 * Route: /user_profile/security/smsVerification
 *
 * Upfront verification priority:
 *   1. Passkey
 *   2. Google Authenticator
 *   3. Email
 *   4. Phone (current mobile — only when changing)
 *
 * "Choose other verification method" shows remaining available methods.
 */
const COUNTRY_OPTIONS = [
  { flag: "🇦🇪", code: "+971", label: "UAE", iso: "ae" },
  { flag: "🇮🇳", code: "+91", label: "India", iso: "in" },
  { flag: "🇺🇸", code: "+1", label: "United States", iso: "us" },
  { flag: "🇬🇧", code: "+44", label: "United Kingdom", iso: "gb" },
  { flag: "🇸🇦", code: "+966", label: "Saudi Arabia", iso: "sa" },
  { flag: "🇵🇰", code: "+92", label: "Pakistan", iso: "pk" },
  { flag: "🇧🇩", code: "+880", label: "Bangladesh", iso: "bd" },
];

function maskEmail(email) {
  if (!email) return "";
  const [u, d] = String(email).split("@");
  if (!d) return email;
  if (u.length <= 2) return `${u[0]}***@${d}`;
  return `${u.slice(0, 2)}***${u.slice(-1)}@${d}`;
}

function maskPhone(phone) {
  if (!phone) return "";
  const s = String(phone);
  if (s.length <= 4) return `***${s.slice(-2)}`;
  return `${s.slice(0, 2)}${"*".repeat(Math.max(s.length - 4, 1))}${s.slice(-2)}`;
}

const SmsVerification = () => {
  const navigate = useNavigate();
  const profileCtx = useContext(ProfileContext) || {};
  const userDetails = useMemo(() => profileCtx.userDetails || {}, [profileCtx.userDetails]);
  const handleUserDetails = profileCtx.handleUserDetails || (() => { });

  const emailId = userDetails?.emailId || "";
  const currentMobile = userDetails?.mobileNumber || "";
  const hasEmail = !!emailId;
  const hasMobile = !!currentMobile;
  const hasGoogleAuth = (userDetails?.["2fa"] || 0) === 2;

  const [country, setCountry] = useState(COUNTRY_OPTIONS[0]);
  const [isCountryOpen, setIsCountryOpen] = useState(false);
  const countryRef = useRef(null);

  // Core form state
  const [newMobile, setNewMobile] = useState("");
  const [mobileSmsCode, setMobileSmsCode] = useState("");

  // ── Upfront verification state (tracks which method popup is active) ──
  const [activeUpfrontMethod, setActiveUpfrontMethod] = useState(null); // 'email' | 'authenticator' | 'phone' | null
  const [isSecurityRiskMethodsOpen, setIsSecurityRiskMethodsOpen] = useState(false);

  // Passkey
  const [passkeySupported, setPasskeySupported] = useState(false);
  const [hasPasskey, setHasPasskey] = useState(false);
  const [passkeyVerifiedUpfront, setPasskeyVerifiedUpfront] = useState(null);
  const [passkeyListResolved, setPasskeyListResolved] = useState(false);

  // Email upfront
  const [emailOtpCells, setEmailOtpCells] = useState(["", "", "", "", "", ""]);
  const [emailOtpCapturedValue, setEmailOtpCapturedValue] = useState("");
  const [emailVerifiedUpfront, setEmailVerifiedUpfront] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [isVerifyingEmail, setIsVerifyingEmail] = useState(false);
  const [emailResendTimer, setEmailResendTimer] = useState(0);

  // Google Authenticator upfront
  const [authenticatorOtpCells, setAuthenticatorOtpCells] = useState(["", "", "", "", "", ""]);
  const [authenticatorCodeCaptured, setAuthenticatorCodeCaptured] = useState("");
  const [authenticatorVerifiedUpfront, setAuthenticatorVerifiedUpfront] = useState(false);

  // Phone (current mobile) upfront — only for change flow
  const [currentPhoneOtpCells, setCurrentPhoneOtpCells] = useState(["", "", "", "", "", ""]);
  const [phoneOtpCapturedValue, setPhoneOtpCapturedValue] = useState("");
  const [phoneVerifiedUpfront, setPhoneVerifiedUpfront] = useState(false);
  const [phoneResendTimer, setPhoneResendTimer] = useState(0);

  // New-mobile SMS resend timer + loading
  const [smsResendTimer, setSmsResendTimer] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  // Refs
  const initUpfrontDone = useRef(false);
  const initEmailOtpSent = useRef(false);
  const initPhoneOtpSent = useRef(false);

  const isEmailOtpComplete = emailOtpCells.every((d) => d !== "");
  const isAuthenticatorOtpComplete = authenticatorOtpCells.every((d) => d !== "");
  const isPhoneOtpComplete = currentPhoneOtpCells.every((d) => d !== "");
  const purpose = hasMobile ? "change_mobile" : "add_mobile";

  const isAnyUpfrontVerified = !!(
    passkeyVerifiedUpfront ||
    authenticatorVerifiedUpfront ||
    emailVerifiedUpfront ||
    phoneVerifiedUpfront
  );

  // ════════════════════════════════════════════════════════════════════
  // Callbacks
  // ════════════════════════════════════════════════════════════════════

  const checkPasskeySupport = useCallback(async () => {
    if (window.PublicKeyCredential === undefined || typeof window.PublicKeyCredential !== "function") {
      setPasskeySupported(false);
      return;
    }
    try {
      const available = await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
      setPasskeySupported(!!available);
    } catch {
      setPasskeySupported(false);
    }
  }, []);

  const fetchPasskeys = useCallback(async () => {
    try {
      const result = await AuthService.passkeyGetList();
      if (result?.success && result?.data != null) {
        const d = result.data;
        const n =
          d.count != null ? Number(d.count) : Array.isArray(d.passkeys) ? d.passkeys.length : 0;
        setHasPasskey(n > 0);
      }
    } catch {
      // silent
    } finally {
      setPasskeyListResolved(true);
    }
  }, []);

  const attemptSilentPasskeyVerification = useCallback(async () => {
    try {
      const optionsResult = await AuthService.passkeyGetStepUpOptions();
      if (!optionsResult?.success || !optionsResult?.data) return null;
      let credential;
      try {
        credential = await startAuthentication(optionsResult.data);
      } catch {
        return null;
      }
      const verifyResult = await AuthService.passkeyVerifyStepUp(credential);
      if (verifyResult?.success) return verifyResult.data;
      return null;
    } catch {
      return null;
    }
  }, []);

  const sendEmailOtpUpfront = useCallback(async () => {
    if (!hasEmail) return false;
    try {
      setIsLoading(true);
      LoaderHelper.loaderStatus(true);
      const result = await AuthService.securitySendOtp("email", purpose);
      if (result?.success) {
        alertSuccessMessage(result?.message || "Verification code sent to your email");
        setEmailResendTimer(60);
        return true;
      }
      alertErrorMessage(result?.message || "Failed to send email code");
      return false;
    } catch (error) {
      alertErrorMessage(error?.message || "Failed to send email code");
      return false;
    } finally {
      setIsLoading(false);
      LoaderHelper.loaderStatus(false);
    }
  }, [hasEmail, purpose]);

  const sendPhoneOtpUpfront = useCallback(async () => {
    if (!hasMobile) return false;
    try {
      setIsLoading(true);
      LoaderHelper.loaderStatus(true);
      const result = await AuthService.securitySendOtp("mobile", purpose);
      if (result?.success) {
        alertSuccessMessage(result?.message || "Verification code sent to your phone");
        setPhoneResendTimer(60);
        return true;
      }
      alertErrorMessage(result?.message || "Failed to send phone code");
      return false;
    } catch (error) {
      alertErrorMessage(error?.message || "Failed to send phone code");
      return false;
    } finally {
      setIsLoading(false);
      LoaderHelper.loaderStatus(false);
    }
  }, [hasMobile, purpose]);

  // ════════════════════════════════════════════════════════════════════
  // Effects
  // ════════════════════════════════════════════════════════════════════

  useEffect(() => {
    checkPasskeySupport();
    fetchPasskeys();
  }, [checkPasskeySupport, fetchPasskeys]);

  // Root prereq: email must be verified to add/change mobile
  useEffect(() => {
    if (!userDetails) return;
    if (!hasEmail) {
      alertErrorMessage(
        hasMobile
          ? "Please verify your email first before changing your mobile number."
          : "Please verify your email first before adding a mobile number."
      );
      navigate("/user_profile/security/emailVerification", { replace: true });
    }
  }, [userDetails, hasEmail, hasMobile, navigate]);

  /**
   * Priority-based upfront verification on mount.
   * Waits for passkey list to resolve, then auto-triggers the highest priority method:
   *   1. Passkey (silent)  →  2. Authenticator (popup)  →  3. Email (popup)  →  4. Phone (popup)
   */
  useEffect(() => {
    if (!hasEmail && !hasMobile) return;
    if (!passkeyListResolved) return;
    if (initUpfrontDone.current) return;
    initUpfrontDone.current = true;

    (async () => {
      // Priority 1 — Passkey
      if (hasPasskey && passkeySupported) {
        const vr = await attemptSilentPasskeyVerification();
        if (vr) {
          setPasskeyVerifiedUpfront(vr);
          alertSuccessMessage("Passkey verified");
          return;
        }
        // Passkey attempt failed — fall through to next priority
      }

      // Priority 2 — Google Authenticator
      if (hasGoogleAuth) {
        setActiveUpfrontMethod("authenticator");
        requestAnimationFrame(() => document.getElementById("smsv-auth-otp-0")?.focus());
        return;
      }

      // Priority 3 — Email
      if (hasEmail) {
        if (!initEmailOtpSent.current) {
          initEmailOtpSent.current = true;
          const ok = await sendEmailOtpUpfront();
          if (ok) {
            setActiveUpfrontMethod("email");
            requestAnimationFrame(() => document.getElementById("smsv-email-otp-0")?.focus());
          }
        }
        return;
      }

      // Priority 4 — Phone (current mobile — only when changing)
      if (hasMobile) {
        if (!initPhoneOtpSent.current) {
          initPhoneOtpSent.current = true;
          const ok = await sendPhoneOtpUpfront();
          if (ok) {
            setActiveUpfrontMethod("phone");
            requestAnimationFrame(() => document.getElementById("smsv-phone-otp-0")?.focus());
          }
        }
      }
    })();
  }, [
    hasEmail, hasMobile, hasPasskey, passkeySupported, hasGoogleAuth,
    passkeyListResolved, attemptSilentPasskeyVerification, sendEmailOtpUpfront, sendPhoneOtpUpfront,
  ]);

  // Country dropdown close
  useEffect(() => {
    if (!isCountryOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onPointerDown = (e) => {
      const el = e.target;
      if (countryRef.current?.contains(el)) return;
      setIsCountryOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [isCountryOpen]);

  // Resend timers
  useEffect(() => {
    if (smsResendTimer <= 0) return undefined;
    const t = setTimeout(() => setSmsResendTimer((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [smsResendTimer]);

  useEffect(() => {
    if (emailResendTimer <= 0) return undefined;
    const t = setTimeout(() => setEmailResendTimer((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [emailResendTimer]);

  useEffect(() => {
    if (phoneResendTimer <= 0) return undefined;
    const t = setTimeout(() => setPhoneResendTimer((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [phoneResendTimer]);

  // ════════════════════════════════════════════════════════════════════
  // Generic OTP cell helpers
  // ════════════════════════════════════════════════════════════════════
  const makeOtpInput = (setCells, prefix) => (idx, e) => {
    const v = String(e?.target?.value ?? "").replace(/\D/g, "").slice(0, 1);
    setCells((prev) => {
      const next = [...prev];
      next[idx] = v;
      return next;
    });
    if (v && idx < 5) {
      document.getElementById(`${prefix}-${idx + 1}`)?.focus();
    }
  };

  const makeOtpKeyDown = (cells, prefix) => (idx, e) => {
    if (e.key === "Backspace" && !cells[idx] && idx > 0) {
      document.getElementById(`${prefix}-${idx - 1}`)?.focus();
    }
  };

  const makeOtpInputPaste = (setCells, prefix) => (e) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData("text");
    const digits = String(pasteData || "").replace(/\D/g, "").slice(0, 6).split("");
    if (digits.length > 0) {
      setCells((prev) => {
        const next = [...prev];
        digits.forEach((d, i) => {
          if (i < 6) next[i] = d;
        });
        return next;
      });
      window.requestAnimationFrame(() => {
        const targetIdx = digits.length === 6 ? 5 : digits.length;
        document.getElementById(`${prefix}-${targetIdx}`)?.focus();
      });
    }
  };

  const makeOtpPaste = (setCells, prefix) => async () => {
    try {
      const text = await navigator.clipboard.readText();
      const digits = String(text || "").replace(/\D/g, "").slice(0, 6).split("");
      if (digits.length < 6) return;
      setCells([digits[0], digits[1], digits[2], digits[3], digits[4], digits[5]]);
      document.getElementById(`${prefix}-5`)?.focus();
    } catch {
      // silent
    }
  };

  // Email OTP handlers
  const handleEmailOtpInput = makeOtpInput(setEmailOtpCells, "smsv-email-otp");
  const handleEmailOtpKeyDown = makeOtpKeyDown(emailOtpCells, "smsv-email-otp");
  const handlePasteEmailOtp = makeOtpPaste(setEmailOtpCells, "smsv-email-otp");
  const handleInputPasteEmailOtp = makeOtpInputPaste(setEmailOtpCells, "smsv-email-otp");

  // Authenticator OTP handlers
  const handleAuthOtpInput = makeOtpInput(setAuthenticatorOtpCells, "smsv-auth-otp");
  const handleAuthOtpKeyDown = makeOtpKeyDown(authenticatorOtpCells, "smsv-auth-otp");
  const handlePasteAuthOtp = makeOtpPaste(setAuthenticatorOtpCells, "smsv-auth-otp");
  const handleInputPasteAuthOtp = makeOtpInputPaste(setAuthenticatorOtpCells, "smsv-auth-otp");

  // Phone OTP handlers
  const handlePhoneOtpInput = makeOtpInput(setCurrentPhoneOtpCells, "smsv-phone-otp");
  const handlePhoneOtpKeyDown = makeOtpKeyDown(currentPhoneOtpCells, "smsv-phone-otp");
  const handlePastePhoneOtp = makeOtpPaste(setCurrentPhoneOtpCells, "smsv-phone-otp");
  const handleInputPastePhoneOtp = makeOtpInputPaste(setCurrentPhoneOtpCells, "smsv-phone-otp");

  // ════════════════════════════════════════════════════════════════════
  // Upfront verify confirm / resend / close
  // ════════════════════════════════════════════════════════════════════
  const handleResendEmailOtpUpfront = async () => {
    if (emailResendTimer > 0) return;
    await sendEmailOtpUpfront();
  };

  const handleConfirmEmailUpfront = async () => {
    if (!isEmailOtpComplete) return;
    const otp = emailOtpCells.join("");
    // Capture without consuming (backend will consume on the final API call)
    setEmailOtpCapturedValue(otp);
    setEmailVerifiedUpfront(true);
    setActiveUpfrontMethod(null);
    alertSuccessMessage("Email verification recorded");
  };

  const handleConfirmAuthenticatorUpfront = () => {
    if (!isAuthenticatorOtpComplete) return;
    const code = authenticatorOtpCells.join("");
    setAuthenticatorCodeCaptured(code);
    setAuthenticatorVerifiedUpfront(true);
    setActiveUpfrontMethod(null);
    alertSuccessMessage("Authenticator verification recorded");
  };

  const handleResendPhoneOtpUpfront = async () => {
    if (phoneResendTimer > 0) return;
    await sendPhoneOtpUpfront();
  };

  const handleConfirmPhoneUpfront = async () => {
    if (!isPhoneOtpComplete) return;
    const otp = currentPhoneOtpCells.join("");
    setPhoneOtpCapturedValue(otp);
    setPhoneVerifiedUpfront(true);
    setActiveUpfrontMethod(null);
    alertSuccessMessage("Phone verification recorded");
  };

  const closeUpfrontVerify = () => {
    setActiveUpfrontMethod(null);
    setIsSecurityRiskMethodsOpen(false);
    setEmailOtpCells(["", "", "", "", "", ""]);
    setAuthenticatorOtpCells(["", "", "", "", "", ""]);
    setCurrentPhoneOtpCells(["", "", "", "", "", ""]);
    // Redirect back to main Security Settings page on close
    navigate("/user_profile/security", { replace: true });
  };

  // ════════════════════════════════════════════════════════════════════
  // "Choose other verification method" — all available minus the active one
  // ════════════════════════════════════════════════════════════════════
  const availableMethods = useMemo(() => {
    const list = [];

    // 1. Passkey
    if (hasPasskey && passkeySupported) {
      list.push({
        id: "passkey",
        label: "Passkey",
        icon: "/images/security/passkey_icon.svg",
        onClick: async () => {
          setIsSecurityRiskMethodsOpen(false);
          if (activeUpfrontMethod === "passkey") return;
          LoaderHelper.loaderStatus(true);
          try {
            const vr = await attemptSilentPasskeyVerification();
            if (vr) {
              setPasskeyVerifiedUpfront(vr);
              setActiveUpfrontMethod(null);
              alertSuccessMessage("Passkey verified");
            } else {
              alertErrorMessage("Passkey could not be used. Please try another method.");
            }
          } finally {
            LoaderHelper.loaderStatus(false);
          }
        },
      });
    }

    // 2. Google Authenticator
    if (hasGoogleAuth) {
      list.push({
        id: "authenticator",
        label: "Google Authenticator",
        icon: "/images/authenticator_icon.svg",
        onClick: () => {
          setIsSecurityRiskMethodsOpen(false);
          if (activeUpfrontMethod === "authenticator") return; // already active
          setAuthenticatorOtpCells(["", "", "", "", "", ""]);
          setActiveUpfrontMethod("authenticator");
          requestAnimationFrame(() => document.getElementById("smsv-auth-otp-0")?.focus());
        },
      });
    }

    // 3. Email
    if (hasEmail) {
      list.push({
        id: "email",
        label: "Email verification",
        icon: "/images/email_icon.svg",
        onClick: async () => {
          setIsSecurityRiskMethodsOpen(false);
          if (activeUpfrontMethod === "email") return; // already active
          setEmailOtpCells(["", "", "", "", "", ""]);
          await sendEmailOtpUpfront();
          setActiveUpfrontMethod("email");
          requestAnimationFrame(() => document.getElementById("smsv-email-otp-0")?.focus());
        },
      });
    }

    // 4. Phone (current mobile) — only available when changing
    if (hasMobile) {
      list.push({
        id: "phone",
        label: "Phone verification",
        icon: "/images/phone_icon.svg",
        onClick: async () => {
          setIsSecurityRiskMethodsOpen(false);
          if (activeUpfrontMethod === "phone") return; // already active
          setCurrentPhoneOtpCells(["", "", "", "", "", ""]);
          await sendPhoneOtpUpfront();
          setActiveUpfrontMethod("phone");
          requestAnimationFrame(() => document.getElementById("smsv-phone-otp-0")?.focus());
        },
      });
    }

    return list;
  }, [
    hasPasskey, passkeySupported, hasGoogleAuth, hasEmail, hasMobile,
    activeUpfrontMethod, attemptSilentPasskeyVerification, sendEmailOtpUpfront, sendPhoneOtpUpfront,
  ]);

  // ════════════════════════════════════════════════════════════════════
  // Send SMS to new mobile
  // ════════════════════════════════════════════════════════════════════
  const handleSendSms = async () => {
    if (!newMobile || newMobile.length < 6) {
      alertErrorMessage("Please enter your mobile number first.");
      return;
    }
    const fullPhone = `${country.code}${newMobile}`;
    if (!isValidPhoneNumber(fullPhone)) {
      alertErrorMessage("Please enter a valid phone number for the selected country.");
      return;
    }
    if (smsResendTimer > 0) return;
    try {
      setIsLoading(true);
      LoaderHelper.loaderStatus(true);
      const result = await AuthService.securitySendOtp("new_mobile", purpose, fullPhone);
      if (result?.success) {
        alertSuccessMessage(result?.message || "SMS code sent");
        setSmsResendTimer(60);
      } else {
        alertErrorMessage(result?.message || "Failed to send SMS code");
      }
    } catch (error) {
      alertErrorMessage(error?.message || "Something went wrong");
    } finally {
      setIsLoading(false);
      LoaderHelper.loaderStatus(false);
    }
  };

  // ════════════════════════════════════════════════════════════════════
  // Confirm → complete mobile change
  // ════════════════════════════════════════════════════════════════════
  const openSecurityVerification = async () => {
    if (!isAnyUpfrontVerified) {
      alertErrorMessage("Please complete identity verification first.");
      // Re-open the highest priority method popup
      if (hasPasskey && passkeySupported) {
        // try passkey again
        LoaderHelper.loaderStatus(true);
        try {
          const vr = await attemptSilentPasskeyVerification();
          if (vr) {
            setPasskeyVerifiedUpfront(vr);
            alertSuccessMessage("Passkey verified");
          } else if (hasGoogleAuth) {
            setActiveUpfrontMethod("authenticator");
          } else if (hasEmail) {
            setActiveUpfrontMethod("email");
          } else if (hasMobile) {
            setActiveUpfrontMethod("phone");
          }
        } finally {
          LoaderHelper.loaderStatus(false);
        }
      } else if (hasGoogleAuth) {
        setActiveUpfrontMethod("authenticator");
      } else if (hasEmail) {
        setActiveUpfrontMethod("email");
      } else if (hasMobile) {
        setActiveUpfrontMethod("phone");
      }
      return;
    }
    if (!newMobile || newMobile.length < 6) {
      alertErrorMessage("Please enter your mobile number first.");
      return;
    }
    const fullPhone = `${country.code}${newMobile}`;
    if (!isValidPhoneNumber(fullPhone)) {
      alertErrorMessage("Please enter a valid phone number for the selected country.");
      return;
    }
    if (!mobileSmsCode || mobileSmsCode.length !== 6) {
      alertErrorMessage("Please enter the 6-digit SMS code sent to your mobile.");
      return;
    }

    // All checks passed — complete directly (no separate TOTP step)
    await completeMobileChange();
  };

  // ════════════════════════════════════════════════════════════════════
  // Complete add / change mobile
  // ════════════════════════════════════════════════════════════════════
  const completeMobileChange = async () => {
    try {
      setIsLoading(true);
      LoaderHelper.loaderStatus(true);

      if (hasMobile) {
        // Change flow: Initiate → Complete
        const initData = {
          newMobileNumber: newMobile,
          newCountryCode: country.code,
        };
        if (passkeyVerifiedUpfront) {
          initData.passkeyVerified = true;
          initData.passkeyUserId = passkeyVerifiedUpfront.userId;
        } else if (authenticatorVerifiedUpfront && authenticatorCodeCaptured) {
          initData.tofaCode = authenticatorCodeCaptured;
        } else if (emailVerifiedUpfront && emailOtpCapturedValue) {
          initData.currentEmailOtp = emailOtpCapturedValue;
        } else if (phoneVerifiedUpfront && phoneOtpCapturedValue) {
          initData.currentMobileOtp = phoneOtpCapturedValue;
        }

        const initResult = await AuthService.securityMobileChangeInitiate(initData);
        if (!initResult?.success) {
          alertErrorMessage(initResult?.message || "Failed to initiate mobile change");
          return;
        }
        const completeResult = await AuthService.securityMobileChangeComplete({ newMobileOtp: mobileSmsCode });
        if (completeResult?.success) {
          alertSuccessMessage(completeResult?.message || "Mobile number updated successfully");
          resetAndClose();
          handleUserDetails();
          navigate("/user_profile/security", { replace: true });
        } else {
          alertErrorMessage(completeResult?.message || "Failed to complete mobile change");
        }
      } else {
        // Add flow: securityMobileAdd
        const payload = {
          mobileNumber: newMobile,
          countryCode: country.code,
          mobileOtp: mobileSmsCode,
        };
        if (passkeyVerifiedUpfront) {
          payload.passkeyVerified = true;
          payload.passkeyUserId = passkeyVerifiedUpfront.userId;
        } else if (authenticatorVerifiedUpfront && authenticatorCodeCaptured) {
          payload.tofaCode = authenticatorCodeCaptured;
        } else if (emailVerifiedUpfront && emailOtpCapturedValue) {
          payload.emailOtp = emailOtpCapturedValue;
        } else if (phoneVerifiedUpfront && phoneOtpCapturedValue) {
          payload.currentMobileOtp = phoneOtpCapturedValue;
        }
        const result = await AuthService.securityMobileAdd(payload);
        if (result?.success) {
          alertSuccessMessage(result?.message || "Mobile number added successfully");
          resetAndClose();
          handleUserDetails();
          navigate("/user_profile/security", { replace: true });
        } else {
          alertErrorMessage(result?.message || "Failed to add mobile number");
        }
      }
    } catch (error) {
      alertErrorMessage(error?.message || "Something went wrong");
    } finally {
      setIsLoading(false);
      LoaderHelper.loaderStatus(false);
    }
  };

  const resetAndClose = () => {
    setActiveUpfrontMethod(null);
    setNewMobile("");
    setMobileSmsCode("");
    setEmailOtpCapturedValue("");
    setAuthenticatorCodeCaptured("");
    setPhoneOtpCapturedValue("");
    setSmsResendTimer(0);
    setEmailResendTimer(0);
    setPhoneResendTimer(0);
  };

  const maskedEmailDisplay = maskEmail(emailId);
  const maskedPhoneDisplay = maskPhone(currentMobile);

  // ════════════════════════════════════════════════════════════════════
  // JSX
  // ════════════════════════════════════════════════════════════════════
  return (
    <main className="smsv-page" aria-labelledby="smsv-title">
      <nav className="smsv-page__crumbs" aria-label="Breadcrumb">
        <ol className="smsv-page__crumbList">
          <li className="smsv-page__crumbItem">
            <button type="button" className="smsv-page__crumbLink" onClick={() => navigate("/user_profile/security")}>
              Security
            </button>
          </li>
          <li className="smsv-page__crumbSep" aria-hidden="true">
            ›
          </li>
          <li className="smsv-page__crumbItem smsv-page__crumbItem--active" aria-current="page">
            SMS verification
          </li>
        </ol>
      </nav>

      <section className="smsv-form" aria-label="SMS verification form">
        <h1 id="smsv-title" className="smsv-page__title">
          {hasMobile ? "Change mobile phone verification" : "Link mobile phone verification"}
        </h1>
        <label className="smsv-field">
          <span className="smsv-label">Phone</span>
          <div className="smsv-phoneRow">
            <div className="smsv-countrySelect" ref={countryRef}>
              <button
                type="button"
                className="smsv-countryBtn"
                aria-haspopup="listbox"
                aria-expanded={isCountryOpen}
                onClick={() => setIsCountryOpen((v) => !v)}
              >
                <span className="smsv-flag" aria-hidden="true" style={{ display: 'flex' }}>
                  {country.iso ? (
                    <img src={`https://flagcdn.com/w20/${country.iso}.png`} alt={country.label} style={{ width: 20, height: 'auto', borderRadius: 2 }} />
                  ) : (
                    country.flag
                  )}
                </span>
                <span className="smsv-code">{country.code}</span>
                <span className="smsv-caret" aria-hidden="true">
                  ▾
                </span>
              </button>

              {isCountryOpen ? (
                <div className="smsv-countryMenu" role="listbox" aria-label="Select country code">
                  {COUNTRY_OPTIONS.map((opt) => (
                    <button
                      key={opt.code}
                      type="button"
                      className={`smsv-countryOption${opt.code === country.code ? " is-active" : ""}`}
                      role="option"
                      aria-selected={opt.code === country.code}
                      onClick={() => {
                        setCountry(opt);
                        setIsCountryOpen(false);
                      }}
                    >
                      <span className="smsv-flag" aria-hidden="true" style={{ display: 'flex' }}>
                        {opt.iso ? (
                          <img src={`https://flagcdn.com/w20/${opt.iso}.png`} alt={opt.label} style={{ width: 20, height: 'auto', borderRadius: 2 }} />
                        ) : (
                          opt.flag
                        )}
                      </span>
                      <span className="smsv-countryLabel">{opt.label}</span>
                      <span className="smsv-countryCode">{opt.code}</span>
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
            <input
              className="smsv-input"
              placeholder="Enter phone number"
              inputMode="tel"
              value={newMobile}
              onChange={(e) => setNewMobile(e.target.value.replace(/\D/g, ""))}
              disabled={isLoading}
            />
          </div>
        </label>

        <label className="smsv-field">
          <span className="smsv-label">SMS Code</span>
          <div className="smsv-smsRow">
            <input
              className="smsv-input smsv-input--sms"
              placeholder="Enter the code Sent to your phone"
              inputMode="numeric"
              maxLength={6}
              value={mobileSmsCode}
              onChange={(e) => setMobileSmsCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              disabled={isLoading}
            />
            <button
              type="button"
              className="smsv-sendBtn"
              onClick={handleSendSms}
              disabled={isLoading || smsResendTimer > 0}
            >
              {smsResendTimer > 0 ? `Resend (${smsResendTimer}s)` : "Send"}
            </button>
          </div>
          <div className="smsv-hint">Valid for 10 minutes</div>
        </label>

        <button
          type="button"
          className="smsv-confirmBtn"
          onClick={openSecurityVerification}
          disabled={isLoading}
        >
          {isLoading ? "Please wait…" : "Confirm"}
        </button>
      </section>

      {/* ════════ Upfront: Email Verification popup ════════ */}
      {activeUpfrontMethod === "email" ? (
        <div className="passkey-modal-overlay" role="presentation">
          <div
            className="passkey-modal passkey-verify-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="smsv-verify-email-title"
          >
            <button
              type="button"
              className="passkey-modal__close"
              aria-label="Close"
              onClick={closeUpfrontVerify}
            >
              ×
            </button>

            <h2 id="smsv-verify-email-title" className="passkey-verify-modal__title">
              Verify Your Email
            </h2>
            <p className="passkey-verify-modal__subtitle">
              The verification code has been sent to your email {maskedEmailDisplay}, valid for 10 minutes.
            </p>

            <div className="passkey-verify-modal__otpRow" aria-label="Verification code">
              {emailOtpCells.map((digit, idx) => (
                <input
                  // eslint-disable-next-line react/no-array-index-key
                  key={idx}
                  id={`smsv-email-otp-${idx}`}
                  className="passkey-verify-modal__otp"
                  value={digit}
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={1}
                  autoComplete={idx === 0 ? "one-time-code" : undefined}
                  aria-label={`Digit ${idx + 1}`}
                  onInput={(e) => handleEmailOtpInput(idx, e)}
                  onChange={(e) => handleEmailOtpInput(idx, e)}
                  onKeyDown={(e) => handleEmailOtpKeyDown(idx, e)}
                  onPaste={handleInputPasteEmailOtp}
                />
              ))}
            </div>

            <div className="passkey-verify-modal__links">
              <button
                type="button"
                className="passkey-verify-modal__link"
                onClick={handleResendEmailOtpUpfront}
                disabled={emailResendTimer > 0 || isVerifyingEmail}
                style={emailResendTimer > 0 ? { opacity: 0.55, cursor: "not-allowed", textDecoration: "none" } : undefined}
              >
                {emailResendTimer > 0 ? `Resend in ${emailResendTimer}s` : "Resend"}
              </button>
              <button
                type="button"
                className="passkey-verify-modal__link passkey-verify-modal__link--paste"
                onClick={handlePasteEmailOtp}
              >
                Paste{" "}
                <span aria-hidden="true">
                  <i className="ri-file-copy-fill" />
                </span>
              </button>
            </div>

            <button
              type="button"
              className="passkey-verify-modal__confirm"
              disabled={!isEmailOtpComplete || isVerifyingEmail}
              onClick={handleConfirmEmailUpfront}
            >
              {isVerifyingEmail ? "Verifying…" : "Confirm"}
            </button>

            <div className="passkey-verify-modal__footerRow">
              {availableMethods.length > 0 ? (
                <button
                  type="button"
                  className="passkey-verify-modal__chooseOther"
                  onClick={() => setIsSecurityRiskMethodsOpen(true)}
                >
                  Choose other verification method
                </button>
              ) : null}
              <button
                type="button"
                className="passkey-verify-modal__unable"
                onClick={() =>
                  navigate("/asset_managemnet/security_verification_unavailable", {
                    state: { preselectMethods: ["phone"] },
                  })
                }
              >
                Security verification unavailable?
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* ════════ Upfront: Google Authenticator popup ════════ */}
      {activeUpfrontMethod === "authenticator" ? (
        <div className="passkey-modal-overlay" role="presentation">
          <div
            className="passkey-modal passkey-verify-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="smsv-verify-auth-title"
          >
            <button
              type="button"
              className="passkey-modal__close"
              aria-label="Close"
              onClick={closeUpfrontVerify}
            >
              ×
            </button>

            <h2 id="smsv-verify-auth-title" className="passkey-verify-modal__title">
              Google Authenticator
            </h2>
            <p className="passkey-verify-modal__subtitle">
              Enter the 6-digit code from your Google Authenticator app.
            </p>

            <div className="passkey-verify-modal__otpRow" aria-label="Authenticator code">
              {authenticatorOtpCells.map((digit, idx) => (
                <input
                  // eslint-disable-next-line react/no-array-index-key
                  key={idx}
                  id={`smsv-auth-otp-${idx}`}
                  className="passkey-verify-modal__otp"
                  value={digit}
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={1}
                  aria-label={`Digit ${idx + 1}`}
                  onInput={(e) => handleAuthOtpInput(idx, e)}
                  onChange={(e) => handleAuthOtpInput(idx, e)}
                  onKeyDown={(e) => handleAuthOtpKeyDown(idx, e)}
                  onPaste={handleInputPasteAuthOtp}
                />
              ))}
            </div>

            <div className="passkey-verify-modal__links passkey-verify-modal__links--totp">
              <button
                type="button"
                className="passkey-verify-modal__link passkey-verify-modal__link--paste"
                onClick={handlePasteAuthOtp}
              >
                Paste{" "}
                <span aria-hidden="true">
                  <i className="ri-file-copy-fill" />
                </span>
              </button>
            </div>

            <button
              type="button"
              className="passkey-verify-modal__confirm"
              disabled={!isAuthenticatorOtpComplete}
              onClick={handleConfirmAuthenticatorUpfront}
            >
              Confirm
            </button>

            <div className="passkey-verify-modal__footerRow">
              {availableMethods.length > 0 ? (
                <button
                  type="button"
                  className="passkey-verify-modal__chooseOther"
                  onClick={() => setIsSecurityRiskMethodsOpen(true)}
                >
                  Choose other verification method
                </button>
              ) : null}
              <button
                type="button"
                className="passkey-verify-modal__unable"
                onClick={() =>
                  navigate("/asset_managemnet/security_verification_unavailable", {
                    state: { preselectMethods: ["phone"] },
                  })
                }
              >
                Security verification unavailable?
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* ════════ Upfront: Phone (current mobile) Verification popup ════════ */}
      {activeUpfrontMethod === "phone" ? (
        <div className="passkey-modal-overlay" role="presentation">
          <div
            className="passkey-modal passkey-verify-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="smsv-verify-phone-title"
          >
            <button
              type="button"
              className="passkey-modal__close"
              aria-label="Close"
              onClick={closeUpfrontVerify}
            >
              ×
            </button>

            <h2 id="smsv-verify-phone-title" className="passkey-verify-modal__title">
              Verify Your Phone
            </h2>
            <p className="passkey-verify-modal__subtitle">
              A verification code has been sent to {maskedPhoneDisplay}, valid for 10 minutes.
            </p>

            <div className="passkey-verify-modal__otpRow" aria-label="Phone verification code">
              {currentPhoneOtpCells.map((digit, idx) => (
                <input
                  // eslint-disable-next-line react/no-array-index-key
                  key={idx}
                  id={`smsv-phone-otp-${idx}`}
                  className="passkey-verify-modal__otp"
                  value={digit}
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={1}
                  autoComplete={idx === 0 ? "one-time-code" : undefined}
                  aria-label={`Digit ${idx + 1}`}
                  onInput={(e) => handlePhoneOtpInput(idx, e)}
                  onChange={(e) => handlePhoneOtpInput(idx, e)}
                  onKeyDown={(e) => handlePhoneOtpKeyDown(idx, e)}
                  onPaste={handleInputPastePhoneOtp}
                />
              ))}
            </div>

            <div className="passkey-verify-modal__links">
              <button
                type="button"
                className="passkey-verify-modal__link"
                onClick={handleResendPhoneOtpUpfront}
                disabled={phoneResendTimer > 0}
                style={phoneResendTimer > 0 ? { opacity: 0.55, cursor: "not-allowed", textDecoration: "none" } : undefined}
              >
                {phoneResendTimer > 0 ? `Resend in ${phoneResendTimer}s` : "Resend"}
              </button>
              <button
                type="button"
                className="passkey-verify-modal__link passkey-verify-modal__link--paste"
                onClick={handlePastePhoneOtp}
              >
                Paste{" "}
                <span aria-hidden="true">
                  <i className="ri-file-copy-fill" />
                </span>
              </button>
            </div>

            <button
              type="button"
              className="passkey-verify-modal__confirm"
              disabled={!isPhoneOtpComplete}
              onClick={handleConfirmPhoneUpfront}
            >
              Confirm
            </button>

            <div className="passkey-verify-modal__footerRow">
              {availableMethods.length > 0 ? (
                <button
                  type="button"
                  className="passkey-verify-modal__chooseOther"
                  onClick={() => setIsSecurityRiskMethodsOpen(true)}
                >
                  Choose other verification method
                </button>
              ) : null}
              <button
                type="button"
                className="passkey-verify-modal__unable"
                onClick={() =>
                  navigate("/asset_managemnet/security_verification_unavailable", {
                    state: { preselectMethods: ["phone"] },
                  })
                }
              >
                Security verification unavailable?
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* ════════ Choose other verification method popup ════════ */}
      {isSecurityRiskMethodsOpen && availableMethods.length > 0 ? (
        <div className="passkey-modal-overlay passkey-modal-overlay--onTop" role="presentation">
          <div
            className="passkey-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="smsv-security-risk-title"
          >
            <button
              type="button"
              className="passkey-modal__close"
              aria-label="Close"
              onClick={() => setIsSecurityRiskMethodsOpen(false)}
            >
              ×
            </button>
            <div className="passkey-modal__art" aria-hidden="true">
              <img className="dark_img" src="/images/security/security_risk_vector.svg" alt="" />
              <img className="light_img" src="/images/security/security_risk_vector_light.svg" alt="" />
            </div>
            <h2 id="smsv-security-risk-title" className="passkey-modal__title">
              Security Risk Warning
            </h2>
            <p className="passkey-modal__subtitle">
              To enhance your account security, please activate at least one additional verification method.
            </p>
            <div className="passkey-modal__list" role="list">
              {availableMethods.map((method) => (
                <button
                  key={method.id}
                  type="button"
                  className="passkey-modal__item"
                  role="listitem"
                  onClick={method.onClick}
                >
                  <span className="passkey-modal__itemLeft">
                    <span className="passkey-modal__itemIcon" aria-hidden="true">
                      <img src={method.icon} alt="" />
                    </span>
                    <span className="passkey-modal__itemText">{method.label}</span>
                  </span>
                  <span className="passkey-modal__chev" aria-hidden="true">
                    ›
                  </span>
                </button>
              ))}
            </div>
            <button
              type="button"
              className="passkey-verify-modal__unable"
              style={{ marginTop: 16 }}
              onClick={() =>
                navigate("/asset_managemnet/security_verification_unavailable", {
                  state: { preselectMethods: ["phone"] },
                })
              }
            >
              Security verification unavailable?
            </button>
          </div>
        </div>
      ) : null}
    </main>
  );
};

export default SmsVerification;
